/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import range from 'lodash/range';

import {WarningEl} from 'common/components/warning';
import {InputEl} from 'common/components/input';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SpinnerEl} from 'common/components/spinner';
import {FormattedProperty} from 'common/util/format-property';

import {ProfileEditablePropertyEl} from './profile-editable-property';
import type {FilteredProperty, ProfileDemographics} from './index';

const Loading = () => (
  <div sel='.content.mp-skeleton-wrapper'>
    {range(6).map((_) => (
      <div sel='.mp-panel-section'>
        <div sel='.mp-skeleton-large' />
      </div>
    ))}
  </div>
);

export function template(this: ProfileDemographics) {
  const {isCreatingNewPropertyLoading, searchText, showCreateNewProperty} = this.state;
  const {t} = this.getContext(`i18n`);

  const FilteredProperties = ({
    filteredProperties,
    propertiesLoading,
  }: {
    filteredProperties: FilteredProperty[];
    propertiesLoading: string[];
  }) => (
    <>
      {filteredProperties.map(({property, matchParts}) => (
        <div sel='.mp-panel-section'>
          <ProfileEditablePropertyEl
            key={`${this.helpers.getRawPropertyName(property)}`}
            attrs={{
              loading: propertiesLoading.includes(this.helpers.getRawPropertyName(property)),
              property,
              'sidebar-mode': this.attr(`sidebar-mode`),
              'match-parts': matchParts,
              'can-write-profile': this.attr(`can-write-profile`),
              'data-group-id': this.attr(`data-group-id`),
              'distinct-id': this.attr(`distinct-id`),
            }}
            on={{
              update: this.helpers.handlePropertyUpdate,
              delete: this.helpers.handlePropertyDelete,
            }}
          />
        </div>
      ))}
    </>
  );

  const AllProperties = ({
    allProperties,
    propertiesLoading,
  }: {
    allProperties: FormattedProperty[];
    propertiesLoading: string[];
  }) => (
    <>
      {allProperties.map((property) => (
        <div sel='.mp-panel-section'>
          <ProfileEditablePropertyEl
            key={`${this.helpers.getRawPropertyName(property)}`}
            attrs={{
              loading: propertiesLoading.includes(this.helpers.getRawPropertyName(property)),
              property,
              'sidebar-mode': this.attr(`sidebar-mode`),
              'can-write-profile': this.attr(`can-write-profile`),
              'data-group-id': this.attr(`data-group-id`),
              'distinct-id': this.attr(`distinct-id`),
            }}
            on={{
              update: this.helpers.handlePropertyUpdate,
              delete: this.helpers.handlePropertyDelete,
            }}
          />
        </div>
      ))}
    </>
  );

  const CreateNewProperty = () => (
    <div sel='.mp-panel-section.new-property-input'>
      <div sel='.new-property-title'>{t(`profile.create-prop`)}</div>
      <InputEl
        sel='.name-input'
        attrs={{
          label: t(`profile.prop-name`),
        }}
        on={{
          input: this.helpers.handleUpdateNewPropertyName,
        }}
        hook={{
          insert: ({elm}) => (elm as HTMLElement).focus(),
        }}
      />
      <InputEl
        sel='.value-input'
        attrs={{
          label: t(`profile.prop-value`),
        }}
        on={{
          input: this.helpers.handleUpdateNewPropertyValue,
        }}
      />
      <div sel='.confirmation-buttons'>
        {isCreatingNewPropertyLoading ? (
          <SpinnerEl />
        ) : (
          <>
            <ButtonEl
              sel='.new-property-button'
              attrs={{
                theme: ButtonTheme.Tertiary,
                elref: `profileCancelNewProperty`,
              }}
              on={{
                click: this.helpers.handleCancelNewProperty,
              }}
            >
              {t(`common.cancel`)}
            </ButtonEl>
            <ButtonEl
              sel='.new-property-button'
              attrs={{
                theme: ButtonTheme.Primary,
                elref: `profileSaveNewProperty`,
              }}
              on={{
                click: this.helpers.handleSaveNewProperty,
              }}
            >
              {t(`common.save`)}
            </ButtonEl>
          </>
        )}
      </div>
    </div>
  );

  const Properties = () => {
    if (this.helpers.isPropertiesLoading()) {
      return <Loading />;
    }
    if (this.attr(`user-properties-error`)) {
      return (
        <WarningEl
          attrs={{
            'header-text': `Unable to Display Properties`,
            'body-text': `Please try again later.`,
            theme: `error`,
          }}
        />
      );
    }
    if (!this.attr(`profile-exists`)) {
      return (
        <WarningEl
          attrs={{
            'header-text': t(`profile.no-props`),
            'cta-text': t(`profile.setup-props`),
            'cta-href': this.helpers.hasDataGroupId()
              ? `https://developer.mixpanel.com/reference/group-profiles#group-set-property`
              : `https://developer.mixpanel.com/reference/user-profiles#profile-set`,
            theme: `no-icon`,
          }}
        />
      );
    }

    return (
      <>
        {!showCreateNewProperty && (
          <div sel='.profile-demographics-property-management' attrs={{elref: `profileProperties`}}>
            <InputEl
              sel='.search-input'
              attrs={{
                icon: `search`,
                placeholder: t(`profile.search-props`),
                value: searchText,
                elref: `profileSearchBar`,
                tabindex: 0,
              }}
              props={{
                value: searchText,
              }}
              on={{
                input: this.helpers.handleUpdateInput,
              }}
            />
          </div>
        )}
        <div sel='.content'>
          {!showCreateNewProperty && !this.attr(`sidebar-mode`) && (
            <div class={{'create-disabled': !this.attr(`can-write-profile`)}}>
              {!this.attr(`can-write-profile`) && (
                <TooltipEl
                  attrs={{
                    placement: Placement.Bottom,
                  }}
                >
                  {t(`profile.create-no-perms`)}
                </TooltipEl>
              )}
              <ButtonEl
                sel='.create-new-property-button'
                attrs={{
                  icon: `plus`,
                  theme: ButtonTheme.Tertiary,
                  elref: `profileNewProperty`,
                  disabled: !this.attr(`can-write-profile`),
                }}
                on={{
                  click: this.helpers.handleCreatePropertyButton,
                }}
              >
                {t(`profile.create-prop`)}
              </ButtonEl>
            </div>
          )}
          {showCreateNewProperty && <CreateNewProperty />}
          {searchText !== `` ? (
            <FilteredProperties
              filteredProperties={this.helpers.getFilteredProperties()}
              propertiesLoading={this.helpers.getPropertiesThatAreLoading()}
            />
          ) : (
            <AllProperties
              allProperties={this.helpers.getAllDisplayProperties()}
              propertiesLoading={this.helpers.getPropertiesThatAreLoading()}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <div sel='.profile-demographics'>
      {!this.attr(`sidebar-mode`) && (
        <div sel='.mp-panel-section.properties-header'>{this.helpers.getPropertiesHeader()}</div>
      )}
      <Properties />
    </div>
  );
}
